'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { BorderCard, Container, Grid, Heading, Skeleton } from '@nutralab/ui';

import { classNames } from '@m1work/shared-utils';

import styles from './HomeSkeleton.module.scss';

const STORY_PLACEHOLDERS = 6;
const PRODUCT_PLACEHOLDERS = 3;

/** The home page while it loads: the banner block, the stories and the first row of products. */
export function HomeSkeleton(): React.ReactElement {
    const { t } = useTranslation();

    return (
        <div aria-busy="true" className={styles.page}>
            <div className={classNames(styles.top, 'mx-3 md:mx-8')}>
                <Grid columns={{ base: 1, md: 2 }} gap={8}>
                    <Skeleton className={styles.links} />
                    <Skeleton className={styles.search} />
                </Grid>

                <div className={styles.banners}>
                    <Skeleton className={styles.banner} />
                    <Skeleton className={classNames(styles.banner, styles['second-banner'])} />
                </div>

                <div className={classNames(styles.stories, 'mt-4 md:mt-16')}>
                    {Array.from({ length: STORY_PLACEHOLDERS }, (_, i) => (
                        <Skeleton key={i} className={styles.story} />
                    ))}
                </div>
            </div>

            <Container className="mt-6 md:mt-20">
                <Skeleton asChild>
                    <Heading level={2} className="mb-4 md:mb-20" size="2xl" align="center" weight="semibold">
                        {t('landing.main-product-list.title')}
                    </Heading>
                </Skeleton>

                <Grid columns={{ base: 1, md: 2, xl: 3 }} gap={8} className="mb-16">
                    {Array.from({ length: PRODUCT_PLACEHOLDERS }, (_, i) => (
                        <BorderCard key={i} className={styles.product}>
                            <Skeleton className={classNames(styles.image, 'mb-4')} />
                            <Skeleton className={classNames(styles.price, 'mb-6')} />
                            <Skeleton className={classNames(styles.name, 'mb-8')} />
                            <Skeleton className={styles.button} />
                        </BorderCard>
                    ))}
                </Grid>
            </Container>
        </div>
    );
}
